import type {
  MapCloudPoint,
  MapPointCloud,
  MapPointCloudSetOptions,
} from '@gpx-spa/geo-domain'
import L from 'leaflet'
import type { MapInteractionController } from '../shared/map-interaction-controller.js'
import { resolvePointCloudThinning } from '../shared/thin-point-cloud-by-min-gap.js'
import { TRACK_VERTEX_UNDER_MARKERS_PANE } from './leaflet-constants.js'

const DEFAULT_RADIUS = 6
const DEFAULT_BORDER = '#ffffff'
const DEFAULT_BORDER_WIDTH = 2
/** Max screen distance (px) from click to point center to count as a hit. */
const HIT_PIXEL_RADIUS = 10

export type LeafletCanvasPointCloudLayerOptions = {
  layerId: string
  getInteractionController?: () => MapInteractionController | null
  /** Standalone mode (public ShipmentMap) when interaction controller is absent. */
  onPointClick?: (point: MapCloudPoint, latlng: L.LatLng, pointIndex: number) => void
  /** Fires for every hit point, including when the interaction controller owns the popup. */
  onPointHit?: (point: MapCloudPoint) => void
}

type IndexedPoint = MapCloudPoint & { index: number }

function ensureTrackVertexPane(map: L.Map): void {
  if (!map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)) {
    map.createPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
    const pane = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE)
    if (pane) {
      pane.style.zIndex = '655'
    }
  }
}

/** Ensures the track-vertex pane exists on a raw Leaflet map (public ShipmentMap). */
export function ensureLeafletTrackVertexPane(map: L.Map): void {
  ensureTrackVertexPane(map)
}

/**
 * Canvas batch renderer for high-count point clouds.
 * Hit-test on click only (brute-force over thinned visible set).
 * Optional thinning: stores the full set and re-thins on zoom (standalone MapLayer parity).
 */
export class LeafletCanvasPointCloudLayer extends L.Layer {
  private fullPoints: MapCloudPoint[] = []
  private points: IndexedPoint[] = []
  private thinningOptions: MapPointCloudSetOptions | undefined
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private hoverNear = false

  constructor(private readonly opts: LeafletCanvasPointCloudLayerOptions) {
    super()
  }

  setPointCloud(cloud: MapPointCloud | null, options?: MapPointCloudSetOptions): void {
    this.fullPoints = cloud?.points ? [...cloud.points] : []
    if (options !== undefined) {
      this.thinningOptions = options
    }
    this.applyVisibleFromFull()
  }

  getPointCloud(): MapPointCloud {
    return { points: this.fullPoints }
  }

  onAdd(map: L.Map): void {
    ensureTrackVertexPane(map)
    const pane = map.getPane(TRACK_VERTEX_UNDER_MARKERS_PANE) ?? map.getPane('overlayPane')
    this.canvas = L.DomUtil.create(
      'canvas',
      'leaflet-canvas-point-cloud',
    ) as HTMLCanvasElement
    const canvas = this.canvas
    canvas.style.position = 'absolute'
    canvas.style.pointerEvents = 'auto'
    canvas.style.zIndex = '655'
    pane?.appendChild(canvas)
    this.ctx = canvas.getContext('2d')
    map.on('moveend zoomend resize viewreset', this.onViewChanged, this)
    map.on('click', this.onMapClick, this)
    map.on('mousemove', this.onMapMove, this)
    this.applyVisibleFromFull()
  }

  onRemove(map: L.Map): void {
    map.off('moveend zoomend resize viewreset', this.onViewChanged, this)
    map.off('click', this.onMapClick, this)
    map.off('mousemove', this.onMapMove, this)
    if (this.canvas?.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas)
    }
    this.canvas = null
    this.ctx = null
    const host = map.getContainer()
    if (host) host.style.cursor = ''
  }

  private applyVisibleFromFull(): void {
    const map = this._map
    const zoom = map?.getZoom() ?? 15
    const visible = resolvePointCloudThinning(
      this.fullPoints,
      zoom,
      this.thinningOptions,
    )
    this.points = visible.map((p, index) => ({ ...p, index }))
    this.redraw()
  }

  private onViewChanged = (): void => {
    this.applyVisibleFromFull()
  }

  private redraw = (): void => {
    const map = this._map
    const canvas = this.canvas
    const ctx = this.ctx
    if (!map || !canvas || !ctx) return

    const size = map.getSize()
    const dpr =
      typeof window !== 'undefined' && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1
    canvas.width = Math.floor(size.x * dpr)
    canvas.height = Math.floor(size.y * dpr)
    canvas.style.width = `${size.x}px`
    canvas.style.height = `${size.y}px`

    const topLeft = map.containerPointToLayerPoint([0, 0])
    L.DomUtil.setPosition(canvas, topLeft)

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size.x, size.y)

    for (const p of this.points) {
      const pt = map.latLngToContainerPoint(
        L.latLng(p.position.lat, p.position.lng),
      )
      const r = p.style.radius ?? DEFAULT_RADIUS
      const border = p.style.borderColor ?? DEFAULT_BORDER
      const bw = p.style.borderWidth ?? DEFAULT_BORDER_WIDTH
      ctx.beginPath()
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2)
      ctx.fillStyle = p.style.fillColor
      ctx.fill()
      ctx.strokeStyle = border
      ctx.lineWidth = bw
      ctx.stroke()
    }
  }

  private findPointAtContainerPoint(
    map: L.Map,
    containerPoint: L.Point,
  ): IndexedPoint | null {
    if (this.points.length === 0) return null
    let best: IndexedPoint | null = null
    let bestDist = HIT_PIXEL_RADIUS
    for (const p of this.points) {
      const pt = map.latLngToContainerPoint(
        L.latLng(p.position.lat, p.position.lng),
      )
      const dx = pt.x - containerPoint.x
      const dy = pt.y - containerPoint.y
      const dist = Math.hypot(dx, dy)
      const hitSlop = (p.style.radius ?? DEFAULT_RADIUS) + 4
      if (dist <= hitSlop && dist < bestDist) {
        bestDist = dist
        best = p
      }
    }
    return best
  }

  private onMapClick = (e: L.LeafletMouseEvent): void => {
    const map = this._map
    if (!map) return
    const point = this.findPointAtContainerPoint(map, e.containerPoint)
    if (!point) return

    L.DomEvent.stopPropagation(e)
    this.opts.onPointHit?.(point)

    const controller = this.opts.getInteractionController?.()
    if (controller?.hasInteractions()) {
      void controller.dispatchHit({
        trigger: 'click',
        latlng: { lat: point.position.lat, lng: point.position.lng },
        nativeEvent: e,
        target: {
          type: 'pointCloud',
          layerId: this.opts.layerId,
          position: { lat: point.position.lat, lng: point.position.lng },
          point,
          pointIndex: point.index,
        },
      })
      return
    }

    this.opts.onPointClick?.(point, e.latlng, point.index)
  }

  private onMapMove = (e: L.LeafletMouseEvent): void => {
    const map = this._map
    if (!map) return
    const near = this.findPointAtContainerPoint(map, e.containerPoint) != null
    if (near === this.hoverNear) return
    this.hoverNear = near
    const host = map.getContainer()
    if (host) host.style.cursor = near ? 'pointer' : ''
  }
}

export function createLeafletCanvasPointCloudLayer(
  options: LeafletCanvasPointCloudLayerOptions,
): LeafletCanvasPointCloudLayer {
  return new LeafletCanvasPointCloudLayer(options)
}
